import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ChartBarIcon,
  CubeIcon,
  TruckIcon,
  UserGroupIcon,
  BanknotesIcon,
  BookOpenIcon,
  Cog6ToothIcon,
  SparklesIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";

export default function HomePage() {
  const username = localStorage.getItem("username") || "User";
  const userRole = localStorage.getItem("userRole") || "guest";
  const [currentTime, setCurrentTime] = useState(new Date());
  const [mounted, setMounted] = useState(false);

  // Update time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  // Mount animation
  useEffect(() => {
    setMounted(true);
  }, []);

  // Define accessible pages by role with proper icons
  const rolePages = {
    admin: [
      {
        name: "Dashboard",
        path: "/dashboard",
        icon: ChartBarIcon,
        description: "Analytics & Insights",
        color: "from-blue-500 to-blue-600"
      },
      {
        name: "Clients",
        path: "/clients",
        icon: UserGroupIcon,
        description: "Manage customers",
        color: "from-purple-500 to-purple-600"
      },
      {
        name: "Products",
        path: "/products",
        icon: CubeIcon,
        description: "Product catalog",
        color: "from-green-500 to-green-600"
      },
      {
        name: "Delivery",
        path: "/delivery",
        icon: TruckIcon,
        description: "Delivery management",
        color: "from-orange-500 to-orange-600"
      },
      {
        name: "Production",
        path: "/production",
        icon: Cog6ToothIcon,
        description: "Production runs",
        color: "from-indigo-500 to-indigo-600"
      },
      {
        name: "Stock Management",
        path: "/stock-management",
        icon: CubeIcon,
        description: "Inventory tracking 🧂",
        color: "from-teal-500 to-teal-600"
      },
      {
        name: "Accounting",
        path: "/accounting",
        icon: BanknotesIcon,
        description: "Income & Expenses",
        color: "from-emerald-500 to-emerald-600"
      },
      {
        name: "Ledger",
        path: "/ledger",
        icon: BookOpenIcon,
        description: "Client balances",
        color: "from-red-500 to-red-600"
      },
    ],
    manager: [
      {
        name: "Clients",
        path: "/clients",
        icon: UserGroupIcon,
        description: "Manage customers",
        color: "from-purple-500 to-purple-600"
      },
      {
        name: "Products",
        path: "/products",
        icon: CubeIcon,
        description: "Product catalog",
        color: "from-green-500 to-green-600"
      },
      {
        name: "Delivery",
        path: "/delivery",
        icon: TruckIcon,
        description: "Delivery management",
        color: "from-orange-500 to-orange-600"
      },
      {
        name: "Production",
        path: "/production",
        icon: Cog6ToothIcon,
        description: "Production runs",
        color: "from-indigo-500 to-indigo-600"
      },
      {
        name: "Stock Management",
        path: "/stock-management",
        icon: CubeIcon,
        description: "Inventory tracking 🧂",
        color: "from-teal-500 to-teal-600"
      },
    ],
    driver: [
      {
        name: "Delivery",
        path: "/delivery",
        icon: TruckIcon,
        description: "My deliveries",
        color: "from-orange-500 to-orange-600"
      },
    ],
    production_staff: [
      {
        name: "Production",
        path: "/production",
        icon: Cog6ToothIcon,
        description: "Production runs",
        color: "from-indigo-500 to-indigo-600"
      },
    ],
    partner: [
      {
        name: "Products",
        path: "/products",
        icon: CubeIcon,
        description: "View products",
        color: "from-green-500 to-green-600"
      },
      {
        name: "Clients",
        path: "/clients",
        icon: UserGroupIcon,
        description: "View clients",
        color: "from-purple-500 to-purple-600"
      },
    ],
    default: [
      {
        name: "Products",
        path: "/products",
        icon: CubeIcon,
        description: "View products",
        color: "from-green-500 to-green-600"
      },
      {
        name: "Delivery",
        path: "/delivery",
        icon: TruckIcon,
        description: "Deliveries",
        color: "from-orange-500 to-orange-600"
      },
    ],
  };

  const pages = rolePages[userRole] || rolePages.default;

  // Greeting based on time
  const getGreeting = () => {
    const hour = currentTime.getHours();
    if (hour < 12) return "Good Morning";
    if (hour < 18) return "Good Afternoon";
    return "Good Evening";
  };

  // Format time
  const formatTime = () => {
    return currentTime.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false
    });
  };

  // Format date
  const formatDate = () => {
    return currentTime.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric"
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F5E9D8] via-[#F5E9D8] to-[#E0D3C0]">
      {/* Decorative background elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-1/2 -right-1/2 w-full h-full bg-[#2D2F5E] opacity-5 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-1/2 -left-1/2 w-full h-full bg-[#F76B4C] opacity-5 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div
          className={`text-center mb-12 transition-all duration-1000 ${mounted ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-10"
            }`}
        >
          {/* Time & Date Card */}
          <div className="inline-flex items-center space-x-4 bg-white bg-opacity-60 backdrop-blur-sm px-6 py-3 rounded-full shadow-lg border border-[#2D2F5E] border-opacity-10 mb-6">
            <ClockIcon className="h-5 w-5 text-[#2D2F5E]" />
            <div className="text-left">
              <div className="text-2xl font-bold text-[#2D2F5E]">{formatTime()}</div>
              <div className="text-xs text-[#2D2F5E] opacity-75">{formatDate()}</div>
            </div>
          </div>

          {/* Greeting */}
          <div className="mb-4">
            <h1 className="text-4xl sm:text-5xl font-bold text-[#2D2F5E] mb-2">
              {getGreeting()}, {username}! 👋
            </h1>
            <p className="text-lg text-[#2D2F5E] opacity-75">
              Welcome back to Pyszna Bakery ERP
            </p>
          </div>

          {/* Role Badge */}
          <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-[#F76B4C] to-[#E65A3C] text-white px-6 py-2 rounded-full shadow-lg">
            <SparklesIcon className="h-5 w-5" />
            <span className="font-semibold capitalize">{userRole}</span>
          </div>
        </div>

        {/* Quick Access Section */}
        <div
          className={`transition-all duration-1000 delay-200 ${mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
            }`}
        >
          <h2 className="text-2xl font-bold text-[#2D2F5E] mb-6 text-center">
            Quick Access
          </h2>

          {/* Cards Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
            {pages.map((page, index) => {
              const Icon = page.icon;
              return (
                <Link
                  key={index}
                  to={page.path}
                  className="group block"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="h-full bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-[#2D2F5E] border-opacity-10 overflow-hidden transform hover:-translate-y-2 hover:scale-105">
                    {/* Gradient Header */}
                    <div className={`bg-gradient-to-r ${page.color} p-4 relative overflow-hidden`}>
                      <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
                      <Icon className="h-8 w-8 text-white relative z-10" />
                    </div>

                    {/* Content */}
                    <div className="p-5">
                      <h3 className="font-bold text-lg text-[#2D2F5E] mb-2 group-hover:text-[#F76B4C] transition-colors duration-300">
                        {page.name}
                      </h3>
                      <p className="text-sm text-[#2D2F5E] opacity-75">
                        {page.description}
                      </p>

                      {/* Arrow indicator */}
                      <div className="flex items-center mt-4 text-[#F76B4C] group-hover:translate-x-2 transition-transform duration-300">
                        <span className="text-sm font-medium mr-2">Go</span>
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>

                    {/* Hover effect overlay */}
                    <div className="absolute inset-0 border-2 border-[#F76B4C] opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div
          className={`text-center mt-12 transition-all duration-1000 delay-600 ${mounted ? "opacity-100" : "opacity-0"
            }`}
        >
          <div className="inline-flex items-center space-x-2 text-[#2D2F5E] opacity-75">
            <div className="h-2 w-2 bg-green-400 rounded-full animate-pulse"></div>
            <span className="text-sm">
              System Online • Secure • Role-Based Access
            </span>
          </div>
          <div className="text-xs text-[#2D2F5E] opacity-50 mt-2">
            © {new Date().getFullYear()} Pyszna Food Bakery ERP
          </div>
        </div>
      </div>
    </div>
  );
}